import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingBag, Minus, Plus, ChevronLeft, Star, Truck, Shield, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { isAuthenticated, openAuthModal } = useAuth();
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  
  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: '',
    author_name: '',
    author_email: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const lang = i18n.language;
        const res = await axios.get(`${API_URL}/api/products/${productId}?lang=${lang}`);
        setProduct(res.data);
        
        // Fetch related products with language
        const relatedRes = await axios.get(`${API_URL}/api/products?category=${res.data.category_id}&limit=4&lang=${lang}`);
        setRelatedProducts(relatedRes.data.filter(p => p.product_id !== productId));
        
        // Fetch reviews
        const reviewsRes = await axios.get(`${API_URL}/api/reviews/product/${productId}`);
        setReviews(reviewsRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [productId, i18n.language]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      toast.info(t('auth.loginToAdd') || 'Connectez-vous pour ajouter au panier');
      return;
    }

    try {
      await addToCart(product.product_id, quantity);
      toast.success(`${product.name} ${t('cart.addedSuccess') || 'ajouté au panier'}`);
    } catch (e) {
      toast.error(t('cart.addError') || "Erreur lors de l'ajout au panier");
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim() || !reviewForm.author_name.trim() || !reviewForm.author_email.trim()) {
      toast.error(t('form.fillAllFields') || 'Veuillez remplir tous les champs');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, {
        product_id: productId,
        ...reviewForm
      });
      toast.success('Merci pour votre avis ! Il sera publié après modération.');
      setShowReviewForm(false);
      setReviewForm({ rating: 5, comment: '', author_name: '', author_email: '' });
    } catch (e) {
      toast.error("Erreur lors de l'envoi de l'avis");
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
        onClick={() => interactive && onRate && onRate(i + 1)}
      />
    ));
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen pt-28 flex items-center justify-center bg-brand-white">
        <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-28 flex flex-col items-center justify-center bg-brand-white">
        <p className="text-brand-dark/60 mb-4">Produit non trouvé</p>
        <Link to="/products">
          <Button className="bg-brand-gold text-white">Retour aux produits</Button>
        </Link>
      </div>
    );
  }

  const price = product.price_retail;
  
  // Handle image URL - support both uploaded files and external URLs
  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600';
    if (img.startsWith('http://') || img.startsWith('https://')) {
      return img;
    }
    // For paths starting with /api/uploads/, prepend API_URL
    if (img.startsWith('/api/')) {
      return `${API_URL}${img}`;
    }
    // For paths starting with /uploads/, prepend API_URL/api
    if (img.startsWith('/uploads/')) {
      return `${API_URL}/api${img}`;
    }
    return img;
  };
  
  const rawImages = product.images?.length > 0 ? product.images : ['https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600'];
  const images = rawImages.map(img => getImageUrl(img));

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link to="/products" className="inline-flex items-center text-brand-gold hover:text-brand-gold mb-8 transition-colors">
          <ChevronLeft className="h-5 w-5 mr-1" /> Retour aux produits
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="relative aspect-square overflow-hidden rounded-lg bg-brand-cream">
              <img 
                src={images[selectedImage]} 
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
              {product.is_new && (
                <span className="absolute top-4 left-4 bg-brand-gold text-white text-xs font-bold px-3 py-1 uppercase tracking-wider flex items-center gap-1 rounded">
                  Nouveau
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded flex-shrink-0 overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-brand-gold' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <span className="text-brand-gold text-sm uppercase tracking-wider font-accent">
                {product.subcategory || 'Collection'}
              </span>
              <h1 className="font-heading text-4xl font-bold text-brand-black mt-2" data-testid="product-name">
                {product.name}
              </h1>
              
              {/* Rating display */}
              {reviews.length > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex">{renderStars(Math.round(averageRating))}</div>
                  <span className="text-brand-dark/60 text-sm">({averageRating} - {reviews.length} avis)</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <span className="text-brand-gold font-bold text-3xl" data-testid="product-price">
                ${price.toFixed(2)} CAD
              </span>
            </div>

            <p className="text-brand-dark/80 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {product.stock > 0 ? (
                <>
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-green-600 text-sm">En stock</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-red-600 text-sm">Rupture de stock</span>
                </>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            {product.stock > 0 && (
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <div className="flex items-center border border-brand-gold/20 rounded">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 text-brand-dark/80 hover:text-brand-gold transition-colors"
                    data-testid="qty-decrease"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span className="w-16 text-center text-brand-black font-semibold" data-testid="qty-value">{quantity}</span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="p-3 text-brand-dark/80 hover:text-brand-gold transition-colors"
                    data-testid="qty-increase"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                <Button 
                  onClick={handleAddToCart}
                  className="flex-1 bg-brand-gold text-white hover:bg-brand-gold py-6 font-bold uppercase tracking-wider btn-shine rounded"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="h-5 w-5 mr-2" /> Ajouter au panier
                </Button>
              </div>
            )}

            {/* Features */}
            <div className="grid grid-cols-2 gap-4 pt-8 border-t border-brand-gold/20">
              <div className="text-center">
                <Truck className="h-6 w-6 text-brand-gold mx-auto mb-2" />
                <p className="text-brand-dark/60 text-xs">Livraison rapide</p>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 text-brand-gold mx-auto mb-2" />
                <p className="text-brand-dark/60 text-xs">Paiement sécurisé</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Reviews Section */}
        <section className="mt-16 pt-12 border-t border-brand-gold/20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl font-bold text-brand-black">
              Avis clients {reviews.length > 0 && `(${reviews.length})`}
            </h2>
            <Button 
              onClick={() => setShowReviewForm(!showReviewForm)}
              variant="outline"
              className="border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white"
            >
              <Star className="h-4 w-4 mr-2" /> Donner mon avis
            </Button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-brand-cream rounded-lg p-6 mb-8"
            >
              <h3 className="font-heading text-xl text-brand-black mb-4">Votre avis</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <label className="block text-sm text-brand-dark/80 mb-2">Note</label>
                  <div className="flex gap-1">
                    {renderStars(reviewForm.rating, true, (rating) => setReviewForm({...reviewForm, rating}))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-brand-dark/80 mb-2">Votre nom *</label>
                    <Input 
                      value={reviewForm.author_name}
                      onChange={(e) => setReviewForm({...reviewForm, author_name: e.target.value})}
                      placeholder="Jean Dupont"
                      required
                      className="bg-brand-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-brand-dark/80 mb-2">Votre email *</label>
                    <Input 
                      type="email"
                      value={reviewForm.author_email}
                      onChange={(e) => setReviewForm({...reviewForm, author_email: e.target.value})}
                      placeholder="email@exemple.com"
                      required
                      className="bg-brand-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-brand-dark/80 mb-2">Votre commentaire *</label>
                  <Textarea 
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                    placeholder="Partagez votre expérience avec ce produit..."
                    rows={4}
                    required
                    className="bg-brand-white resize-none"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={submittingReview}
                    className="bg-brand-gold text-white hover:bg-brand-gold"
                  >
                    <Send className="h-4 w-4 mr-2" /> 
                    {submittingReview ? 'Envoi...' : 'Envoyer mon avis'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost"
                    onClick={() => setShowReviewForm(false)}
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </motion.div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review, idx) => (
                <motion.div 
                  key={review.review_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-brand-cream rounded-lg p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-brand-black">{review.author_name}</p>
                      <p className="text-brand-dark/50 text-sm">
                        {new Date(review.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex">{renderStars(review.rating)}</div>
                  </div>
                  <p className="text-brand-dark/80">{review.comment}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-brand-cream rounded-lg">
              <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-brand-dark/60">Aucun avis pour ce produit</p>
              <p className="text-brand-dark/50 text-sm mt-1">Soyez le premier à donner votre avis !</p>
            </div>
          )}
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24">
            <h2 className="font-heading text-3xl font-bold text-brand-black mb-8">Vous aimerez aussi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((prod, idx) => (
                <ProductCard key={prod.product_id} product={prod} index={idx} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
